import { useState, useEffect, useCallback, type Dispatch, type SetStateAction } from 'react';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import {
	PACKAGE_NAME,
	TEAM,
	PAGE_SIZE,
	editPriorityEvent,
	createPriorityEvent,
	deletePriorityEvent,
	reorderPriorityEvent,
} from '../../common/constants';
import {
	INITIAL,
	ERROR,
	LOADING,
	SUCCESS,
	type Priority,
	type PrioritiesTablePage,
	type PrioritiesTableState,
	type ReorderHandler,
	type PaginatedPrioritiesData,
	type PriorityCurrentModalState,
	type PriorityCurrentSidebarState,
	type ClosePriorityModal,
	type CloseSidebarModal,
	type CreateAndEditPriorityRequestBody,
	type PriorityFormAsyncErrors,
} from '../../common/types';
import { createPriority } from '../../services/create-priority';
import { deletePriority } from '../../services/delete-priority';
import { DUPLICATE_NAME_ERROR } from '../../services/duplicate-priority-name-error';
import { editPriority } from '../../services/edit-priority';
import { updatePrioritiesOrder } from '../../services/move-priorities';
import {
	fetchSearchPriorities,
	fetchSearchPrioritiesByPageNumber,
} from '../../services/search-priorities';
import { useNotifications } from './notifications';
import { calculateAddPriorityPaginatedPage, getUpdatePriorityRequestParams } from './utils';

export type UsePrioritiesPageReturn = {
	data: PrioritiesTablePage;
	state: PrioritiesTableState;
	currentPageNumber: number;
	focusedPriorityId?: string;
	cloudId: string;
	isAssignPrioritiesModalOpen: boolean;
	currentModal: PriorityCurrentModalState;
	currentSidebar: PriorityCurrentSidebarState;
	asyncFormErrors?: PriorityFormAsyncErrors;
	setAsyncFormErrors: Dispatch<SetStateAction<PriorityFormAsyncErrors>>;
	onFetch: (pageNumber: number) => void;
	onCreateConfirm: (formData: CreateAndEditPriorityRequestBody) => void;
	onEditConfirm: (formData: CreateAndEditPriorityRequestBody) => void;
	onDeleteConfirm: (priority: Priority) => void;
	closePriorityModal: () => void;
	closePrioritySidebar: () => void;
	onReorder: ReorderHandler;
	onAssignPrioritiesModalToggle: () => void;
	setCurrentModal: Dispatch<SetStateAction<PriorityCurrentModalState>>;
	setCurrentSidebar: Dispatch<SetStateAction<PriorityCurrentSidebarState>>;
	onSetPriorityPosition: (priorityId: string, position: number) => Promise<void>;
};

export const initialPrioritiesTableData = {
	items: [],
	pageNumber: 1,
	pageSize: PAGE_SIZE,
	totalItems: 0,
	isLast: false,
};

export const initialPrioritiesData = {
	pages: {},
	pageNumber: 1,
	pageSize: PAGE_SIZE,
	totalItems: 0,
};

export const REORDER_ERROR_FLAG_ID = 'updateReorderPriorityError';
export const EDIT_ERROR_FLAG_ID = 'updateEditPriorityError';
export const EDIT_SUCCESS_FLAG_ID = 'updateEditPrioritySuccess';
export const CREATE_PRIORITY_SUCCESS_FLAG_ID = 'createPrioritySuccess';
export const CREATE_PRIORITY_ERROR_FLAG_ID = 'createPriorityError';

export const usePrioritiesPage = (): UsePrioritiesPageReturn => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [paginatedPriorities, setPaginatedPriorities] =
		useState<PaginatedPrioritiesData>(initialPrioritiesData);
	const [data, setData] = useState<PrioritiesTablePage>(initialPrioritiesTableData);
	const [currentPageNumber, setCurrentPageNumber] = useState(1);
	const [state, setState] = useState<PrioritiesTableState>(INITIAL);

	const {
		showFlagForCreatePrioritySuccess,
		showFlagForCreatePriorityError,
		showFlagForCreatePriorityErrorUknown,
		showFlagForEditPrioritySuccess,
		showFlagForEditPriorityError,
		showFlagForEditPriorityErrorUnknown,
		showFlagForReorderPriorityError,
		showFlagForDeletePriorityFailure,
		showFlagForDeletePriorityStart,
		showFlagForDeletePrioritySuccess,
	} = useNotifications();
	const [focusedPriorityId, setFocusedPriorityId] = useState<string | undefined>(undefined);
	const { cloudId } = useTenantContext();
	const [isAssignPrioritiesModalOpen, setIsAssignPrioritiesModalOpen] = useState<boolean>(false);
	const [currentModal, setCurrentModal] = useState<PriorityCurrentModalState>(null);
	const [currentSidebar, setCurrentSidebar] = useState<PriorityCurrentSidebarState>(null);
	const [asyncFormErrors, setAsyncFormErrors] = useState<PriorityFormAsyncErrors>(null);

	const onFetch = useCallback(
		async (pageNumber = currentPageNumber) => {
			try {
				setState(LOADING);
				const response = await fetchSearchPriorities(pageNumber);
				setPaginatedPriorities(response);
				const currentPagePriorities = response.pages[pageNumber] || [];
				setData({
					items: currentPagePriorities.items,
					pageNumber,
					pageSize: response.pageSize,
					totalItems: response.totalItems,
					isLast: currentPagePriorities.isLast,
				});

				if (currentPagePriorities.items.length <= 0) throw new Error('No priorities found');

				setCurrentPageNumber(pageNumber);
				setState(SUCCESS);
			} catch (error: unknown) {
				setState(ERROR);
			}
		},
		[currentPageNumber],
	);

	const onCreateConfirm = async (formData: CreateAndEditPriorityRequestBody) => {
		try {
			currentSidebar && setCurrentSidebar({ ...currentSidebar, submitting: true });
			await createPriority(formData);
			setAsyncFormErrors(null);
			setCurrentSidebar(null);

			const destinationPage = calculateAddPriorityPaginatedPage(data.totalItems, data.pageSize);
			await onFetch(destinationPage); // display the new priority on the final paginated page

			showFlagForCreatePrioritySuccess(formData.name);

			fireTrackAnalytics(
				createAnalyticsEvent({}),
				`${createPriorityEvent.actionSubject} ${createPriorityEvent.action}`,
				{},
			);
		} catch (error) {
			currentSidebar && setCurrentSidebar({ ...currentSidebar, submitting: false });
			if (error instanceof Error) {
				if (error.message === DUPLICATE_NAME_ERROR) {
					setAsyncFormErrors({
						duplicateName: formData.name,
					});
				} else {
					fireErrorAnalytics({
						error,
						meta: {
							packageName: PACKAGE_NAME,
							teamName: TEAM,
							id: 'usePrioritiesPage.onCreateConfirm',
						},
					});
				}

				showFlagForCreatePriorityError(formData.name);
			} else {
				showFlagForCreatePriorityErrorUknown(formData.name);
			}
		}
	};

	const onEditConfirm = async (formData: CreateAndEditPriorityRequestBody) => {
		if (currentSidebar?.sidebarName !== 'EDIT') return; // for TS

		try {
			currentSidebar && setCurrentSidebar({ ...currentSidebar, submitting: true });
			await editPriority(currentSidebar.priority.id, formData);
			setCurrentSidebar(null);
			setAsyncFormErrors(null);

			showFlagForEditPrioritySuccess(formData.name);
			await onFetch(); // show the updated priority in the list using curren page default

			fireTrackAnalytics(
				createAnalyticsEvent({}),
				`${editPriorityEvent.actionSubject} ${editPriorityEvent.action}`,
				{},
			);
		} catch (error) {
			currentSidebar && setCurrentSidebar({ ...currentSidebar, submitting: false });
			if (error instanceof Error) {
				if (error.message === DUPLICATE_NAME_ERROR) {
					setAsyncFormErrors({
						duplicateName: formData.name,
					});
				} else {
					fireErrorAnalytics({
						error,
						meta: {
							packageName: PACKAGE_NAME,
							teamName: TEAM,
							id: 'usePrioritiesPage.onEditConfirm',
						},
					});
				}

				showFlagForEditPriorityError(formData.name);
			} else {
				showFlagForEditPriorityErrorUnknown(formData.name);
			}
		}
	};

	const onDeleteConfirm = useCallback(
		async (priority: Priority) => {
			try {
				const deletePriorityResponse = await deletePriority(priority.id);
				closePriorityModal();
				switch (deletePriorityResponse.status) {
					case 'COMPLETE':
						showFlagForDeletePrioritySuccess(priority);
						await onFetch();
						break;
					case 'ENQUEUED':
					case 'RUNNING':
						showFlagForDeletePriorityStart(priority);
						break;
					default:
						throw new Error(
							`Unexpected status from delete priority task: ${deletePriorityResponse.status}`,
						);
				}

				fireTrackAnalytics(
					createAnalyticsEvent({}),
					`${deletePriorityEvent.actionSubject} ${deletePriorityEvent.action}`,
					{},
				);
			} catch (error) {
				closePriorityModal();
				if (error instanceof Error) {
					fireErrorAnalytics({
						error,
						meta: {
							packageName: PACKAGE_NAME,
							teamName: TEAM,
							id: 'usePrioritiesPage.onDelete',
						},
					});
				}
				showFlagForDeletePriorityFailure(priority);
			}
		},
		[
			onFetch,
			showFlagForDeletePriorityFailure,
			showFlagForDeletePriorityStart,
			showFlagForDeletePrioritySuccess,
			createAnalyticsEvent,
		],
	);

	const onFetchPage = useCallback(
		async (pageNumber: number) => {
			const { items, isLast } = await fetchSearchPrioritiesByPageNumber(pageNumber);
			setPaginatedPriorities({
				...paginatedPriorities,
				pages: {
					...paginatedPriorities.pages,
					[pageNumber]: {
						items,
						isLast,
					},
				},
			});
			return items;
		},
		[paginatedPriorities],
	);

	/**
	 * Reorders the items based on the provided source and action.
	 * @param priorityId - The priority id of the source item to be reordered.
	 * @param action - The action to perform on the source item ('UP', 'DOWN', 'TOP', 'BOTTOM').
	 * @returns A promise that resolves when the reordering is complete.
	 */
	const onReorder: ReorderHandler = useCallback(
		async (params) => {
			const { priorityId, action } = params;
			const previousItems = action === 'DRAG' ? [...data.items] : undefined;
			try {
				const { requestParams: updatePriorityRequestParams, newPageNumber } =
					await getUpdatePriorityRequestParams(
						paginatedPriorities,
						currentPageNumber,
						params,
						onFetchPage,
					);

				/**
				 * Additional logic is needed here to handle drag and drop. This is needed because the
				 * DynamicTable component:
				 * - Internally updates the order when a user moves an item via drag and drop
				 * - Is forced to re-render when we set the loading state to true, and therefore renders
				 *   the items in the original order (as passed in by the props API)
				 * - Is re-rendered with the updated order after we fetch the new state from the server
				 *   and update our state in this controller
				 *
				 * To avoid this UI jank we overwrite the items to show in the expected order during the
				 * loading state.
				 */
				if (action === 'DRAG') {
					const { dragDestinationIndex, dragSourceIndex } = params;
					const itemToMove = data.items[dragSourceIndex];
					const itemsWithoutItemToMove = data.items.filter((p) => p.id !== priorityId);
					const newItems = [
						...itemsWithoutItemToMove.slice(0, dragDestinationIndex),
						itemToMove,
						...itemsWithoutItemToMove.slice(dragDestinationIndex),
					];

					setState(LOADING);
					setData({
						...data,
						items: newItems,
					});
				}
				setFocusedPriorityId(undefined);
				await updatePrioritiesOrder(updatePriorityRequestParams);
				await onFetch(newPageNumber);
				setCurrentPageNumber(newPageNumber);
				if (action === 'UP' || action === 'DOWN') {
					setFocusedPriorityId(priorityId);
				}

				fireTrackAnalytics(
					createAnalyticsEvent({}),
					`${reorderPriorityEvent.actionSubject} ${reorderPriorityEvent.action}`,
					{},
				);
			} catch (error) {
				if (action === 'DRAG' && previousItems) {
					setData({ ...data, items: previousItems });
					setState('SUCCESS');
				}
				if (error instanceof Error) {
					fireErrorAnalytics({
						error,
						meta: {
							packageName: PACKAGE_NAME,
							teamName: TEAM,
							id: 'onReorder',
						},
					});
				}

				showFlagForReorderPriorityError();
			}
		},
		[
			data,
			paginatedPriorities,
			currentPageNumber,
			onFetchPage,
			onFetch,
			showFlagForReorderPriorityError,
			createAnalyticsEvent,
		],
	);

	const closePriorityModal: ClosePriorityModal = () => {
		setCurrentModal(null);
	};

	const closePrioritySidebar: CloseSidebarModal = () => {
		setCurrentSidebar(null);
	};

	const onAssignPrioritiesModalToggle = () => {
		setIsAssignPrioritiesModalOpen(!isAssignPrioritiesModalOpen);
	};

	const onSetPriorityPosition = useCallback(
		async (priorityId: string, position: number) => {
			await onReorder({ priorityId, position, action: 'SET_POSITION' });
			setCurrentModal(null);
			setFocusedPriorityId(priorityId);
		},
		[onReorder],
	);

	useEffect(() => {
		(async () => {
			await onFetch();
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return {
		data,
		state,
		currentPageNumber,
		focusedPriorityId,
		cloudId,
		isAssignPrioritiesModalOpen,
		currentModal,
		currentSidebar,
		asyncFormErrors,
		setAsyncFormErrors,
		onFetch,
		onCreateConfirm,
		onEditConfirm,
		onDeleteConfirm,
		onReorder,
		closePriorityModal,
		closePrioritySidebar,
		setCurrentModal,
		setCurrentSidebar,
		onSetPriorityPosition,
		onAssignPrioritiesModalToggle,
	};
};
